import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { relativeTime } from '../lib/dateUtils.js'

const TOAST_AUTO_DISMISS_MS = 8000

/**
 * Live ticker. Listens to point_events INSERT and shows dismissible toasts
 * top-right plus a fixed-bottom scroller for the longer recent feed.
 *
 * Toasts auto-dismiss after 8s, can be X'd manually, and won't stack more
 * than 3 deep.
 */
export default function ActivityTicker({ dark = false }) {
  const [events, setEvents] = useState([])
  const [dismissed, setDismissed] = useState(() => new Set())
  const [showStream, setShowStream] = useState(false)
  const mountedRef = useRef(false)

  useEffect(() => {
    if (!supabase) return

    async function loadInitial() {
      const { data } = await supabase
        .from('point_events')
        .select('id, points, reason, created_at, participants(name)')
        .order('created_at', { ascending: false })
        .limit(20)
      // Don't toast historical events on first mount — only ones that arrive
      // live during this session. Otherwise every page load nags the user.
      const initial = (data || []).map(toEvent)
      setEvents(initial)
      setDismissed(new Set(initial.map(e => e.id)))
      mountedRef.current = true
    }
    loadInitial()

    const ch = supabase
      .channel(`ticker-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'point_events' }, async (payload) => {
        const { data } = await supabase
          .from('point_events')
          .select('id, points, reason, created_at, participants(name)')
          .eq('id', payload.new.id)
          .single()
        if (data) setEvents(prev => [toEvent(data), ...prev].slice(0, 50))
      })
      .subscribe()

    return () => { supabase.removeChannel(ch) }
  }, [])

  // Auto-dismiss any visible toast after TOAST_AUTO_DISMISS_MS
  const visibleToasts = events.filter(e => !dismissed.has(e.id)).slice(0, 3)
  useEffect(() => {
    if (!visibleToasts.length) return
    const timers = visibleToasts.map(e =>
      setTimeout(() => dismiss(e.id), TOAST_AUTO_DISMISS_MS),
    )
    return () => timers.forEach(clearTimeout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleToasts.map(e => e.id).join(',')])

  function dismiss(id) {
    setDismissed(prev => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  return (
    <>
      {/* top-right toast stack — dismissible */}
      <div className="fixed top-20 right-4 z-50 flex flex-col gap-2">
        <AnimatePresence>
          {visibleToasts.map(e => (
            <motion.div
              key={e.id}
              initial={{ x: 320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 320, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="bg-white border border-brand-border rounded-lg shadow-card-hover pl-4 pr-2 py-2 max-w-xs flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-brand-text">
                  {e.name} <span className="text-brand-blue">+{e.points}</span>
                </div>
                <div className="text-xs text-brand-text-muted">{e.reason} · {relativeTime(e.created_at)}</div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(e.id)}
                aria-label="Dismiss"
                className="shrink-0 text-brand-text-muted hover:text-brand-text rounded-md w-6 h-6 flex items-center justify-center hover:bg-brand-bg-muted transition-colors"
              >
                ×
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* bottom dismissible scroller */}
      {showStream && (
        <div className={`fixed bottom-0 left-0 right-0 z-40 border-t backdrop-blur ${
          dark ? 'bg-black/60 border-white/10 text-white' : 'bg-white/85 border-brand-border text-brand-text'
        }`}>
          <div className="flex items-center gap-3 overflow-x-auto py-2 px-4 text-xs">
            <span className="font-semibold uppercase tracking-wider text-brand-text-muted shrink-0">Live</span>
            {events.slice(0, 12).map(e => (
              <span key={e.id} className="shrink-0 flex items-center gap-1">
                <span className="font-semibold">{e.name}</span>
                <span className="text-brand-blue font-semibold">+{e.points}</span>
                <span className="text-brand-text-muted">{e.reason}</span>
                <span className="text-brand-text-muted">·</span>
              </span>
            ))}
            <button onClick={() => setShowStream(false)} className="ml-auto shrink-0 text-brand-text-muted hover:text-brand-text">×</button>
          </div>
        </div>
      )}
      {!showStream && (
        <button
          onClick={() => setShowStream(true)}
          className="fixed bottom-4 left-4 z-40 text-xs bg-brand-text text-white px-3 py-1.5 rounded-full shadow-lg hover:bg-brand-text-hover"
        >
          Show live activity
        </button>
      )}
    </>
  )
}

function toEvent(row) {
  return {
    id: row.id,
    points: row.points,
    reason: row.reason,
    created_at: row.created_at,
    name: row.participants?.name || 'Someone',
  }
}
